import escapeHTML from 'escape-html';
import { inlineStyle, parseBackgroundImageUrl, parseChromeColor } from './appBannerChrome';

// What the native app SDKs draw INSIDE the card `appBannerChrome` resolves.
// They never render `banner.html`: sdk-react-native, sdk-kotlin, sdk-swift and
// sdk-flutter all walk the Unlayer design JSON — `body.rows[].columns[]
// .contents[]` — and build native views from it. A port of
// sdk-react-native's `DesignRenderer.tsx`, cross-checked against sdk-kotlin's
// `DesignRenderer.kt`, emitting static markup instead of native views.
//
// Two consequences the preview exists to show:
//   - Only six tools are drawn. An `html`, `social`, `timer`, `form`, `video`
//     or custom tool is skipped in silence on a device, which is what
//     `skippedAppContentNote` reports beside the preview.
//   - Text, headings and buttons are plain strings: the rich markup Unlayer
//     stores in `values.text` is stripped to its text content.
//
// Every value below reaches the markup through one of the parsers here or
// through `escapeHTML`, so nothing a design carries can open a tag or escape a
// style attribute.

// Readable nouns for the skipped list; anything else is named by its own tool
// type, which is what an author sees in the editor's panel.
const TOOL_LABELS = { html: 'HTML block' };

const HEADING_FONT_SIZES = { h1: 32, h2: 28, h3: 24, h4: 20, h5: 18, h6: 16 };
const DEFAULT_HEADING_FONT_SIZE = 32;
const DEFAULT_FONT_SIZE = 14;
const DEFAULT_TEXT_COLOR = '#000000';
const DEFAULT_FONT_FAMILY = 'sans-serif';
const DEFAULT_BUTTON_BACKGROUND = '#3AAEE0';
const DEFAULT_BUTTON_COLOR = '#FFFFFF';
const DEFAULT_BUTTON_PADDING = { top: 10, right: 20, bottom: 10, left: 20 };
const DEFAULT_DIVIDER_WIDTH = 1;
const DEFAULT_DIVIDER_COLOR = '#BBBBBB';
const DEFAULT_PREVIEW_WIDTH = 100;
const CAROUSEL_FALLBACK_RATIO = { width: 16, height: 9 };
const IMAGE_MIN_HEIGHT = 100;
const DOT_SIZE = 8;

// A font stack, as Unlayer stores it (`'Open Sans',sans-serif`). Single quotes
// and commas are the whole of its vocabulary, so anything else is not one.
const FONT_STACK = /^[a-z0-9 ,'-]+$/i;

const list = (value) => (Array.isArray(value) ? value : []);

const px = (value) => `${value}px`;

const parseDimension = (value) => {
  if (value === null || value === undefined) {
    return null;
  }

  const parsed = parseFloat(
    String(value)
      .replace(/[a-zA-Z%]/g, '')
      .trim()
  );
  return Number.isNaN(parsed) ? null : parsed;
};

// A CSS padding shorthand, in the one-to-four-value form the design writes it.
const parsePadding = (value) => {
  const text = String(value ?? '').trim();
  if (!text) {
    return null;
  }

  const parts = text.split(/\s+/).map((part) => parseDimension(part) ?? 0);

  switch (parts.length) {
    case 1:
      return { top: parts[0], right: parts[0], bottom: parts[0], left: parts[0] };
    case 2:
      return { top: parts[0], right: parts[1], bottom: parts[0], left: parts[1] };
    case 3:
      return { top: parts[0], right: parts[1], bottom: parts[2], left: parts[1] };
    case 4:
      return { top: parts[0], right: parts[1], bottom: parts[2], left: parts[3] };
    default:
      return null;
  }
};

// The image a body or row paints behind its content, as the native renderers
// do: `contain` and `custom` fit it whole, anything else covers (the body
// always covers).
const backgroundImageStyle = (backgroundImage, forceCover = false) => {
  const url = parseBackgroundImageUrl(backgroundImage);
  if (url === null) {
    return {};
  }

  const fit = !forceCover && ['contain', 'custom'].includes(backgroundImage.size);

  return {
    'background-image': `url('${escapeHTML(url)}')`,
    'background-size': fit ? 'contain' : 'cover',
    'background-position': 'center',
    'background-repeat': 'no-repeat',
  };
};

const paddingValue = (padding) =>
  padding === null ? null : `${padding.top}px ${padding.right}px ${padding.bottom}px ${padding.left}px`;

const TEXT_ALIGNS = { center: 'center', right: 'right', justify: 'justify' };
const parseTextAlign = (value) => (Object.hasOwn(TEXT_ALIGNS, value) ? TEXT_ALIGNS[value] : 'left');

// Where a shrink-to-fit button sits in its row. `justify` is not an alignment
// for a box, so it reads as the `left` the native renderers fall back to.
const BOX_ALIGNS = { center: 'center', right: 'right' };

// Only an absolute http(s) url: a native `Image` source takes nothing else, and
// a link is only worth drawing as one when the browser can follow it.
const parseAbsoluteUrl = (value) => {
  if (!value) {
    return null;
  }

  let url;
  try {
    url = new URL(String(value));
  } catch {
    return null;
  }

  return /^https?:$/.test(url.protocol) ? url.href : null;
};

const parseFontStack = (value) => {
  const text = String(value ?? '').trim();
  return FONT_STACK.test(text) ? text : null;
};

// sdk-react-native's own `stripHtml`, entity decoding and order included. What
// it returns is text, so it goes back through `escapeHTML` before it is
// written into the document.
const stripHtml = (html) =>
  String(html ?? '')
    .replace(/<[^>]*>/g, '')
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    .replace(/&nbsp;/g, ' ')
    .trim();

const linked = (markup, href, display, fill = {}) => {
  const url = parseAbsoluteUrl(href);

  return url === null
    ? markup
    : `<a href="${escapeHTML(url)}" style="${inlineStyle({
        display,
        ...fill,
        color: 'inherit',
        'text-decoration': 'none',
      })}">${markup}</a>`;
};

const imageMarkup = (values) => {
  const url = parseAbsoluteUrl(values.src?.url);
  if (url === null) {
    return '';
  }

  const image = `<img src="${escapeHTML(url)}" alt="" style="${inlineStyle({
    display: 'block',
    width: '100%',
    'min-height': px(IMAGE_MIN_HEIGHT),
    'object-fit': 'contain',
  })}">`;

  return linked(image, values.action?.values?.href, 'block');
};

const resolvedTextColor = (values, fallback) =>
  parseChromeColor(values.color) ?? parseChromeColor(values.textColor) ?? fallback;

const textMarkup = (values, textColor) => {
  const text = stripHtml(values.text);
  if (text === '') {
    return '';
  }

  return `<div style="${inlineStyle({
    'font-size': px(parseDimension(values.fontSize) ?? DEFAULT_FONT_SIZE),
    'text-align': parseTextAlign(values.textAlign),
    color: resolvedTextColor(values, textColor),
  })}">${escapeHTML(text)}</div>`;
};

const headingMarkup = (values, textColor) => {
  const text = stripHtml(values.text);
  if (text === '') {
    return '';
  }

  const headingSize = Object.hasOwn(HEADING_FONT_SIZES, values.headingType)
    ? HEADING_FONT_SIZES[values.headingType]
    : DEFAULT_HEADING_FONT_SIZE;

  return `<div style="${inlineStyle({
    'font-size': px(parseDimension(values.fontSize) ?? headingSize),
    'font-weight': 'bold',
    'text-align': parseTextAlign(values.textAlign),
    color: resolvedTextColor(values, textColor),
  })}">${escapeHTML(text)}</div>`;
};

const buttonMarkup = (values) => {
  const text = stripHtml(values.text);
  if (text === '') {
    return '';
  }

  const padding = parsePadding(values.padding) ?? DEFAULT_BUTTON_PADDING;
  const autoWidth = values.size?.autoWidth ?? true;

  const box = `<span style="${inlineStyle({
    display: 'inline-block',
    'box-sizing': 'border-box',
    width: autoWidth ? null : '100%',
    padding: paddingValue(padding),
    'border-radius': px(parseDimension(values.borderRadius) ?? 0),
    'background-color': parseChromeColor(values.buttonColors?.backgroundColor) ?? DEFAULT_BUTTON_BACKGROUND,
    color: parseChromeColor(values.buttonColors?.color) ?? DEFAULT_BUTTON_COLOR,
    'font-size': px(parseDimension(values.fontSize) ?? DEFAULT_FONT_SIZE),
    'text-align': 'center',
  })}">${escapeHTML(text)}</span>`;

  return `<div style="${inlineStyle({
    'text-align': BOX_ALIGNS[parseTextAlign(values.textAlign)] ?? 'left',
  })}">${linked(box, values.href?.values?.href, autoWidth ? 'inline-block' : 'block')}</div>`;
};

const dividerMarkup = (values) =>
  `<div style="${inlineStyle({
    width: '100%',
    'border-top': `${parseDimension(values.border?.borderTopWidth) ?? DEFAULT_DIVIDER_WIDTH}px solid ${
      parseChromeColor(values.border?.borderTopColor) ?? DEFAULT_DIVIDER_COLOR
    }`,
  })}"></div>`;

// A pager on a device, its first page here: the track is a row clipped to one
// image at the first image's aspect ratio, with the same dot row — or preview
// strip — under it.
const carouselMarkup = (content) => {
  const values = content.values ?? {};
  const images = list(content.embedded?.images?.values);
  if (images.length === 0) {
    return '';
  }

  const first = images[0]?.src ?? {};
  const ratioWidth = typeof first.width === 'number' ? first.width : CAROUSEL_FALLBACK_RATIO.width;
  const ratioHeight = typeof first.height === 'number' ? first.height : CAROUSEL_FALLBACK_RATIO.height;

  const slides = images
    .map((image) => {
      const url = parseAbsoluteUrl(image?.src?.url);
      const slide =
        url === null
          ? `<div style="${inlineStyle({ width: '100%', height: '100%', 'background-color': '#eee' })}"></div>`
          : `<img src="${escapeHTML(url)}" alt="" style="${inlineStyle({
              display: 'block',
              width: '100%',
              height: '100%',
              'object-fit': 'cover',
            })}">`;

      return `<div style="${inlineStyle({ flex: '0 0 100%', height: '100%' })}">${linked(
        slide,
        image?.action?.values?.href,
        'block',
        { height: '100%' }
      )}</div>`;
    })
    .join('');

  const track = `<div style="${inlineStyle({
    display: 'flex',
    overflow: 'hidden',
    'aspect-ratio': `${ratioWidth}/${ratioHeight}`,
  })}">${slides}</div>`;

  if (images.length < 2) {
    return track;
  }

  if (values.showPreviews === true) {
    const previewWidth = parseDimension(values.previewWidth) ?? DEFAULT_PREVIEW_WIDTH;
    const previews = images
      .map((image, index) => {
        const url = parseAbsoluteUrl(image?.src?.url);

        return `<div style="${inlineStyle({
          flex: `0 0 ${previewWidth}px`,
          height: px(previewWidth * 0.75),
          margin: '0 4px',
          'box-sizing': 'border-box',
          'border-radius': '4px',
          border: `2px solid ${index === 0 ? '#555' : 'transparent'}`,
          overflow: 'hidden',
          'background-color': '#eee',
        })}">${
          url === null
            ? ''
            : `<img src="${escapeHTML(url)}" alt="" style="${inlineStyle({
                display: 'block',
                width: '100%',
                height: '100%',
                'object-fit': 'cover',
              })}">`
        }</div>`;
      })
      .join('');

    return `${track}<div style="${inlineStyle({
      display: 'flex',
      overflow: 'hidden',
      'margin-top': '8px',
      padding: '0 4px',
    })}">${previews}</div>`;
  }

  const dots = images
    .map(
      (image, index) =>
        `<div style="${inlineStyle({
          width: px(DOT_SIZE),
          height: px(DOT_SIZE),
          'border-radius': px(DOT_SIZE / 2),
          margin: '0 4px',
          'background-color': index === 0 ? '#555' : '#ccc',
        })}"></div>`
    )
    .join('');

  return `${track}<div style="${inlineStyle({
    display: 'flex',
    'justify-content': 'center',
    'margin-top': '8px',
  })}">${dots}</div>`;
};

// The six tools the native renderers know, and the only list of them: what is
// not a key here is what `skippedAppContentNote` counts.
const TOOL_MARKUP = {
  image: imageMarkup,
  text: textMarkup,
  heading: headingMarkup,
  button: buttonMarkup,
  // The only one that reads the content node itself: its images hang off
  // `embedded`, not `values`.
  carousel: (values, textColor, content) => carouselMarkup(content),
  divider: dividerMarkup,
};

const DRAWN_TOOLS = new Set(Object.keys(TOOL_MARKUP));

const contentMarkup = (content, textColor) => {
  const values = content?.values ?? {};
  // A tool the apps skip keeps its padded wrapper, empty: both SDKs reserve the space.
  const inner = DRAWN_TOOLS.has(content?.type) ? TOOL_MARKUP[content.type](values, textColor, content) : '';

  const containerPadding = paddingValue(parsePadding(values.containerPadding));

  return containerPadding === null ? inner : `<div style="padding:${containerPadding}">${inner}</div>`;
};

const columnMarkup = (column, textColor) => {
  const values = column?.values ?? {};
  const borderRadius = parseDimension(values.borderRadius);

  return `<div style="${inlineStyle({
    'box-sizing': 'border-box',
    'background-color': parseChromeColor(values.backgroundColor),
    padding: paddingValue(parsePadding(values.padding)),
    'border-radius': borderRadius === null ? null : px(borderRadius),
  })}">${list(column?.contents)
    .map((content) => contentMarkup(content, textColor))
    .join('')}</div>`;
};

const rowMarkup = (row, textColor) => {
  const values = row?.values ?? {};
  const columns = list(row?.columns);
  const cells = list(row?.cells);

  const inner =
    columns.length === 1
      ? columnMarkup(columns[0], textColor)
      : `<div style="${inlineStyle({ display: 'flex', 'align-items': 'flex-start' })}">${columns
          .map(
            (column, index) =>
              // `min-width: 0` is what makes a CSS flex item behave like the
              // Yoga one this ports: without it the item's min-content width
              // is its floor and a wide column would not honour its ratio.
              `<div style="${inlineStyle({
                flex: `${Number.isFinite(cells[index]) ? cells[index] : 1} 1 0%`,
                'min-width': 0,
              })}">${columnMarkup(column, textColor)}</div>`
          )
          .join('')}</div>`;

  return `<div style="${inlineStyle({
    'box-sizing': 'border-box',
    'background-color': parseChromeColor(values.backgroundColor) ?? parseChromeColor(values.columnsBackgroundColor),
    ...backgroundImageStyle(values.backgroundImage),
    padding: paddingValue(parsePadding(values.padding)),
  })}">${inner}</div>`;
};

/**
 * A banner's `design` as the API serves it. An object on the wire — but a
 * banner the author has only just created carries `design: ''` until the
 * editor exports one (`BannersNewForm`), and that is a banner the apps do not
 * draw at all.
 */
export const parseBannerDesign = (design) => {
  let parsed = design;
  if (typeof design === 'string') {
    try {
      parsed = JSON.parse(design);
    } catch {
      return null;
    }
  }

  return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed : null;
};

/** The design's own content, as the native renderers draw it. */
export const appDesignMarkup = (design, { displayType } = {}) => {
  const body = design?.body;
  if (!body) {
    return '';
  }

  const values = body.values ?? {};
  const textColor = parseChromeColor(values.textColor) ?? DEFAULT_TEXT_COLOR;
  // The popup and flyout cards paint the body's background image and the body
  // stays transparent over it; on a bar the body paints it itself.
  const cardPaints = displayType === 'popup' || displayType === 'flyout';
  const overImage = cardPaints && parseBackgroundImageUrl(values.backgroundImage) !== null;

  return `<div style="${inlineStyle({
    width: '100%',
    'background-color': overImage ? 'transparent' : (parseChromeColor(values.backgroundColor) ?? 'transparent'),
    ...(cardPaints ? {} : backgroundImageStyle(values.backgroundImage, true)),
    color: textColor,
    'font-family': parseFontStack(values.fontFamily?.value) ?? DEFAULT_FONT_FAMILY,
  })}">${list(body.rows)
    .map((row) => rowMarkup(row, textColor))
    .join('')}</div>`;
};

/**
 * What the apps leave out of this design, as a sentence — or null when they
 * draw all of it. The author has no other way to find out: a tool the native
 * renderers do not know is dropped without a word.
 */
export const skippedAppContentNote = (design) => {
  const counts = new Map();

  for (const row of list(design?.body?.rows)) {
    for (const column of list(row?.columns)) {
      for (const content of list(column?.contents)) {
        if (typeof content?.type === 'string' && !DRAWN_TOOLS.has(content.type)) {
          counts.set(content.type, (counts.get(content.type) ?? 0) + 1);
        }
      }
    }
  }

  if (counts.size === 0) {
    return null;
  }

  const parts = [...counts].map(([type, count]) => {
    const label = Object.hasOwn(TOOL_LABELS, type) ? TOOL_LABELS[type] : type;
    return `${count} ${count === 1 ? label : `${label}s`}`;
  });

  return `Not shown in the apps: ${parts.join(', ')}.`;
};
